// Per-user frozen workspaces: which users have a live (memory-resident) page tree, which
// pages each of them has visited this session, and which page is active. The React side
// (WorkspacesHost) renders one keep-alive pane per visited page and freezes whole
// workspaces with <Activity> — this store is the single source of truth for that shape.
//
// Mirrors the profile-roster-store pattern: module-level singleton + a custom change event,
// plus an immutable snapshot for useSyncExternalStore. Every mutation replaces the changed
// workspace object AND the root state object — referential stability of the untouched
// workspaces is what lets memoized panes skip re-rendering.
//
// Nothing persists across restarts: every workspace CREATION lands on Dashboard, so the
// announcements feed is what greets each profile's first login of an app session (explicit
// product decision — it replaced the restore-last-page behavior). Mid-session switch-backs
// thaw the frozen tree and return to whatever page that profile was on.

import { getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { isWebMode } from "@/lib/config/web-mode";

const WORKSPACES_CHANGE_EVENT = "healo-workspaces-change";

// LRU caps: at most 4 concurrent frozen workspaces (never evicting the active one), and at
// most 6 keep-alive panes per workspace. Dashboard is exempt from the pane cap — it is the
// landing/lock page and must never be evicted out from under a snap-to-Dashboard.
const MAX_WORKSPACES = 4;
const MAX_PANES = 6;

// Canonical page vocabulary — the only names a workspace may reference. WorkspacesHost keys
// its renderer map off this list; keep the two in sync by importing from here.
export const PAGE_NAMES = Object.freeze([
  "Dashboard",
  "Messages",
  "Chats",
  "Private",
  "Patients",
  "Tasks",
  "Inbox",
  "Calendar",
  "Slots",
  "Sessions",
  "Work Plan",
  "Time Off",
  "Search",
  "Knowledge Base",
  "Trigger Base",
  "Support",
  "Settings",
]);

export function isKnownPageName(page) {
  return PAGE_NAMES.includes(page);
}

// Seed synchronously from the (sessionStorage-restored) session: a renderer reload with a
// live session must paint logged-in on the very first frame — waiting for the auth bridge's
// mount-effect sync would flash the lock screen and churn a throwaway lock-screen instance.
let state = Object.freeze(
  (() => {
    const empty = { activeUserId: "", order: [], workspaces: {} };
    if (typeof window === "undefined") return empty;
    const uid = String(getAuthUser()?.id || "").trim();
    if (!uid) return empty;
    return {
      activeUserId: uid,
      order: [uid],
      workspaces: { [uid]: createWorkspace(uid, Date.now()) },
    };
  })(),
);

function emitChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(WORKSPACES_CHANGE_EVENT));
}

function setState(next) {
  state = Object.freeze(next);
  emitChange();
}

export function getWorkspacesSnapshot() {
  return state;
}

export function subscribeWorkspaces(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(state);
  window.addEventListener(WORKSPACES_CHANGE_EVENT, handler);
  return () => window.removeEventListener(WORKSPACES_CHANGE_EVENT, handler);
}

function createWorkspace(uid, now) {
  return {
    activePage: "Dashboard",
    visitedPages: ["Dashboard"],
    lastActiveAt: now,
    pageLastVisit: { Dashboard: now },
  };
}

/**
 * Make `uid` the active workspace, creating a fresh Dashboard-landing tree when it has no
 * live one yet (new profile, evicted, or first activation after restart). Runs the
 * workspace-count LRU eviction — an evicted workspace loses its memory tree, so a later
 * activation starts back on Dashboard like any fresh login.
 */
export function activateWorkspace(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return;
  const now = Date.now();
  const existing = state.workspaces[uid];
  const workspace = existing ? { ...existing, lastActiveAt: now } : createWorkspace(uid, now);
  let order = state.order.includes(uid) ? state.order : [...state.order, uid];
  let workspaces = { ...state.workspaces, [uid]: workspace };
  if (isWebMode()) {
    // Single-user web app: the host only ever renders the active workspace, so drop the
    // rest instead of letting inert other-user trees accumulate in memory.
    order = [uid];
    workspaces = { [uid]: workspace };
  }
  if (order.length > MAX_WORKSPACES) {
    // Oldest lastActiveAt first; the workspace being activated is never a candidate.
    const evictable = order
      .filter((id) => id !== uid)
      .sort((a, b) => (workspaces[a]?.lastActiveAt || 0) - (workspaces[b]?.lastActiveAt || 0));
    while (order.length > MAX_WORKSPACES && evictable.length) {
      const doomed = evictable.shift();
      order = order.filter((id) => id !== doomed);
      const next = { ...workspaces };
      delete next[doomed];
      workspaces = next;
    }
  }
  setState({ activeUserId: uid, order, workspaces });
}

/**
 * Freeze everything: no workspace is active (logged out / session expired). The trees stay
 * memory-resident so a PIN re-entry thaws the same workspace untouched.
 */
export function deactivateWorkspaces() {
  if (!state.activeUserId) return;
  setState({ ...state, activeUserId: "" });
}

/**
 * Navigate the active workspace to `page`: appends a keep-alive pane on first visit, stamps
 * the page-level LRU clock, and evicts the least recently visited pane beyond MAX_PANES
 * (Dashboard exempt, the target page never evicted).
 */
export function setActiveWorkspacePage(page) {
  const uid = state.activeUserId;
  if (!uid) return;
  const name = String(page || "").trim();
  if (!isKnownPageName(name)) return;
  const existing = state.workspaces[uid];
  if (!existing) return;
  const now = Date.now();
  let visitedPages = existing.visitedPages.includes(name)
    ? existing.visitedPages
    : [...existing.visitedPages, name];
  const pageLastVisit = { ...existing.pageLastVisit, [name]: now };
  const countable = visitedPages.filter((p) => p !== "Dashboard");
  if (countable.length > MAX_PANES) {
    const doomed = new Set(
      countable
        .filter((p) => p !== name)
        .sort((a, b) => (pageLastVisit[a] || 0) - (pageLastVisit[b] || 0))
        .slice(0, countable.length - MAX_PANES),
    );
    visitedPages = visitedPages.filter((p) => !doomed.has(p));
    for (const p of doomed) delete pageLastVisit[p];
  }
  const workspace = { ...existing, activePage: name, visitedPages, pageLastVisit };
  setState({ ...state, workspaces: { ...state.workspaces, [uid]: workspace } });
}

/**
 * Drop a page's keep-alive pane from the active workspace (RBAC snap: the page became
 * forbidden and must unmount). Dashboard is never removable. Callers are expected to follow
 * up with setActiveWorkspacePage('Dashboard') when the removed page was the active one.
 */
export function removePageFromActiveWorkspace(page) {
  const uid = state.activeUserId;
  if (!uid) return;
  const name = String(page || "").trim();
  if (!name || name === "Dashboard") return;
  const existing = state.workspaces[uid];
  if (!existing || !existing.visitedPages.includes(name)) return;
  const pageLastVisit = { ...existing.pageLastVisit };
  delete pageLastVisit[name];
  const workspace = {
    ...existing,
    visitedPages: existing.visitedPages.filter((p) => p !== name),
    pageLastVisit,
  };
  setState({ ...state, workspaces: { ...state.workspaces, [uid]: workspace } });
}

/**
 * Destroy a user's workspace memory tree. Used by voluntary logout (privacy) and
 * remove-from-device — NOT by 401 expiry, which only freezes. The next sign-in starts on
 * Dashboard like any fresh login. (profile-roster-store's doom loop still purges the
 * legacy healo-workspace:{uid} snapshot key from pre-Dashboard-landing builds.)
 */
export function destroyWorkspace(userId) {
  const uid = String(userId || "").trim();
  if (!uid) return;
  if (!state.order.includes(uid)) return;
  const workspaces = { ...state.workspaces };
  delete workspaces[uid];
  setState({
    activeUserId: state.activeUserId === uid ? "" : state.activeUserId,
    order: state.order.filter((id) => id !== uid),
    workspaces,
  });
}

let authBridgeRegistered = false;

/**
 * Single auth-driven activation path: every login, PIN switch, credentials switch, logout and
 * 401 expiry flows through one setAuthSession event — uid present activates that workspace,
 * uid absent freezes them all. Registered once from App's mount effect; also syncs the
 * already-restored session (sessionStorage) immediately, since that fires no event.
 */
export function registerWorkspaceAuthBridge() {
  if (authBridgeRegistered || typeof window === "undefined") return;
  authBridgeRegistered = true;
  const sync = () => {
    const uid = String(getAuthUser()?.id || "").trim();
    if (uid) {
      activateWorkspace(uid);
    } else {
      deactivateWorkspaces();
    }
  };
  subscribeAuthStateChange(sync);
  sync();
}
